import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Record as RRecord,
  Runtype,
  Static,
  String,
  Union,
} from "runtypes";

import { OrgRole, OrgRoleLiteral } from "./enums.js";
import { KernelSize } from "./kernelConfigTypes.js";
import { AiCreditLimit, FREE_TIER_USER_CREDITS } from "./magic/credits.js";
import { getNormalEnum } from "./runtypeEnums.js";
import { typedObjectKeys } from "./utils/typedObjects.js";

/*eslint sort-keys: ["error", "asc", {caseSensitive: false, natural: true, allowLineSeparatedGroups: true}] -- expect object keys to be sorted*/
/**
 * FeatureGateSet expresses a set of features that are disabled/enabled
 * for a given product pricing plan, ie. Community, Pro, Enterprise plans.
 *
 * This should not be used to define disabled/enabled features for specific orgs
 * outside the scope of which pricing plan the org is on.
 */
const FeatureGateSetBase = {
  name: String,

  /** Project view graph analytics */
  advancedProjectViewStats: Boolean,
  /**
   * Admins can disable the ability for users to create data connections
   * from anywhere outside of the admin panel. Turning this on does not
   * break existing data connections, but prevents new ones from being created.
   */
  allowDisableAddDataConnectionOutsideAdminPanel: Boolean,
  /** Admins can disable CSV downloads for App-only users at the org level */
  allowDisableCsvDownload: Boolean,
  /**
   * Admins can disable viewing workspace secrets in the projects UI.
   * Yes, it is trivial to expose secrets via code, but this prevents
   * accidental exposure in the UI.
   */
  allowDisableViewWorkspaceSecretsInProjectUI: Boolean,
  allowDisableWorkspaceSharing: Boolean,
  /** Admins can disable file uploads across all projects at the org level */
  allowRestrictFileUploads: Boolean,
  /** App kernel idle timeout in milliseconds */
  appIdleTimeout: Number,
  auditLogs: Boolean,
  /** Admins can configure automatic arhcive criteria */
  autoArchive: Boolean,
  /** @deprecated: Derive chat support availability from Stripe subscription */
  chatSupport: Boolean,
  collections: Boolean,
  components: Boolean,
  configurableKernelSize: Boolean,
  contextStudio: Boolean,
  /** Admins can set per-user/group/workspace AI credit usage limits */
  creditUsageLimits: Boolean,
  customAppIcon: Boolean,
  /** custom code formatting (e.g. sqlfluff) is enabled */
  customCodeFormatting: Boolean,
  /** Admins can configure custom color palettes */
  customColorPalettes: Boolean,
  customCronScheduledRuns: Boolean,
  /** Admins can customize role request messages or disable role requests */
  customOrgRoleRequests: Boolean,
  /** Number of custom published app themes. Null indicates this feature is disabled */
  customPublishedAppThemeLimit: Number.Or(Null),
  /** Admins can configure query permissions for shared data connections */
  databaseQueryPermissions: Boolean,
  /** Whether or not an org can configure and use data retention policies */
  dataRetention: Boolean,
  dbtIntegration: Boolean,
  dbtMetricCell: Boolean,
  /** Default kernel size for new projects */
  defaultKernelSize: KernelSize,
  /** Default Logic kernel idle timeout in milliseconds; for maximum value, see maxFreeLogicIdleTimeout */
  defaultLogicIdleTimeout: Number,
  directorySync: Boolean,
  /** number of days of edit history viewable */
  editHistoryDays: Number,
  /** An org can designate statuses as endorsed */
  endorsements: Boolean,
  /** Explore from a published app is enabled */
  exploreFromHere: Boolean,
  /** Explore from any table is enabled */
  exploreFromScratch: Boolean,
  /** Ability to assign the explorer role to workspace members */
  explorerRole: Boolean,
  /** Access to the public Hex API */
  externalApi: Boolean,
  externalFileIntegrations: Boolean,
  evals: Boolean,
  /** Whether or not the GitHub integration (i.e. package import) is active */
  githubIntegration: Boolean,
  /** Whether or not GitHub sync repository and GitLab sync project support is active */
  githubSync: Boolean, // used for both github and gitlab sync
  /** Whether or not ad-hoc Google Sheets export is available from display tables */
  googleSheetsExport: Boolean,
  /** Whether or not the org can create groups */
  groups: Boolean,
  hexMagic: Boolean,
  /** Whether or not the org can use their own API key for magic */
  magicApiKey: Boolean,
  /**Whether magic typeahead is active */
  magicTypeahead: Boolean,
  /** Maximum Logic kernel idle timeout for free compute profiles in milliseconds; for default value, see defaultLogicIdleTimeout */
  maxFreeLogicIdleTimeout: Number,
  /**
   * Any compute profile with a memory limit larger than this `maxKernelSizes` compute profile will be shown with a tooltip
   * which compute profiles are shown at all is determined by the `kernelSizesForClient` variable
   */
  maxKernelSizes: KernelSize,
  /** Kernel max execution timeout in milliseconds  */
  maxRuntime: Number,
  /** Whether or not the org can access the MCP server */
  mcp: Boolean,
  /** Whether or not the org can use Hex as an MCP client */
  mcpClient: Boolean,
  /** Whether or not an org can use publish reviews at all */
  optionalPublishReviews: Boolean,
  /** Whether or not org domains can be used to auto add users */
  orgDomains: Boolean,
  /** Whether or not org-level files can be created and used */
  orgFiles: Boolean,
  /**
   * Whether or not a project (ie. Hex) can be shared to the org
   *
   * @deprecated
   */
  orgProjectSharing: Boolean,
  /** Whether or not org-level secrets can be created and used */
  orgSecrets: Boolean,
  /** Whether to allow users to upgrade their kernels to paid compute profiles */
  paidCompute: Boolean,
  /** Whether presentation mode is available for published apps */
  presentationMode: Boolean,
  /** Whether or not an org can require publish reviews */
  requiredPublishReviews: Boolean,
  /**
   * Whether or not saved views can be accessed / created.
   */
  savedViews: Boolean,
  scheduledRunLimit: Number,
  /** Whether or not scheduled runs will execute */
  scheduledRuns: Boolean,
  /** Whether scheduled runs can be managed from the admin UI */
  scheduledRunsManager: Boolean,
  /** Whether or not schedules can be configured to include notifications */
  scheduleNotifications: Boolean,
  /** Includes both scheduled run screenshots and on-demand PDF downloads */
  screenshots: Boolean,
  /** Whether an org can create semantic projects or not */
  semanticProjects: Boolean,
  /** Whether or not server git providers (e.g. GHE Server) are active */
  serverGitProviders: Boolean,
  /**
   * Max number of collections sharable with workspace or users + groups, per org, null means infinite. Set to 0 to disable sharing.
   * Value will be disregarded if `collections` feature set field is false
   */
  sharedCollectionLimit: Number.Or(Null),
  /**
   * Max number of components sharable with workspace or users + groups, per org, null means infinite. Set to 0 to disable sharing.
   * Value will be disregarded if `components` feature set field is false
   */
  sharedComponentLimit: Number.Or(Null),
  /** Whether or not scheduled run notifications can be sent to Slack channels */
  slackChannelNotifications: Boolean,
  snowflakeOAuthConnections: Boolean,
  /** Whether or not SSO (not OAuth) is enabled or configurable */
  sso: Boolean,
  /**
   * Whether or not more than 5 categories/statuses can be created
   *
   * @deprecated - no longer checked in FE
   */
  unlimitedProjectLabels: Boolean,
  usersLastActive: Boolean,
  /** number of days of version history viewable, null means infinite */
  versionHistoryDays: Number.Or(Null),
  /** Whether view data for a chart is enabled */
  viewData: Boolean,
  /** Can admins see analytics for workspace assets */
  workspaceUsageAnalytics: Boolean,
};
/*eslint-disable -- expect object keys to be sorted*/

export const FeatureGateSet = RRecord(FeatureGateSetBase);
export type FeatureGateSet = Static<typeof FeatureGateSet>;

export type FeatureGateUpgradeToTierMapping = Record<
  keyof FeatureGateSet,
  FeatureGateTier | null
>;

export type FeatureGate = keyof FeatureGateSet;

export const FeatureGateAddons = Union(
  Literal("signedEmbedding"),
  Literal("agentUsageLimit"),
  Literal("appAgent"),
);
export type FeatureGateAddons = Static<typeof FeatureGateAddons>;

// Add ons are paid additional features that are added to base plans that might not map to a specific tier
export type FeatureGateWithAddons = FeatureGate | FeatureGateAddons;

/**
 * This is the user facing feature gate description used:
 * - To show the user in the contact sales dialog form
 * - To create the a human readable campaign name in salesforce when first seen
 *
 * If a feature gate has a contact sales upsell, it must be registered here.
 */
export const FeatureGateDescriptionForSalesUpsell = {
  auditLogs: "audit logs",
  directorySync: "directory sync",
  allowDisableWorkspaceSharing: "disable workspace sharing",
  allowDisableCsvDownload: "disable CSV downloads",
  allowRestrictFileUploads: "restrict file uploads",
  allowDisableViewWorkspaceSecretsInProjectUI:
    "disable viewing workspace secrets",
  allowDisableAddDataConnectionOutsideAdminPanel:
    "disable non-admin data connection creation",
  customOrgRoleRequests: "customize role request messages",
  customPublishedAppThemeLimit: "additional custom app themes",
  snowflakeOAuthConnections: "OAuth connections",
  databaseQueryPermissions: "database query permissions",
  signedEmbedding: "signed embedding",
  explorerRole: "the explorer role",
  magicApiKey: "use your own OpenAI API key",
  sso: "enable SSO",
  agentUsageLimit: "increase your AI usage limit",
} as const;
export type SalesUpsellFeatureGates =
  keyof typeof FeatureGateDescriptionForSalesUpsell;
export const FeatureGateSalesUpsellOverridesForDialog: Partial<
  Record<
    SalesUpsellFeatureGates,
    {
      title: string;
      description: string;
    }
  >
> = {
  agentUsageLimit: {
    title: "Request more credits",
    description: "Contact our sales team to inquire about extended agent usage",
  },
};

export const SalesUpsellFeatureGateNameWithoutAddons = Union(
  ...(typedObjectKeys(FeatureGateDescriptionForSalesUpsell)
    .filter((k) => !FeatureGateAddons.guard(k))
    .map((k) => Literal(k)) as any),
) as Runtype<
  Exclude<keyof typeof FeatureGateDescriptionForSalesUpsell, FeatureGateAddons>
>;

export const FeatureGateName = Union(
  ...(typedObjectKeys(FeatureGateSetBase).map((k) => Literal(k)) as any),
) as Runtype<keyof typeof FeatureGateSetBase>;

export const FeatureGateNameWithAddons = Union(
  FeatureGateName,
  FeatureGateAddons,
);

export const FeatureGateTierLiteral = Union(
  Literal("COMMUNITY"),
  Literal("PROFESSIONAL"),
  Literal("TEAM"),
  Literal("ENTERPRISE"),
);
export type FeatureGateTier = Static<typeof FeatureGateTierLiteral>;
export const FeatureGateTier = getNormalEnum(FeatureGateTierLiteral);

export const LABELS_BY_FEATURE_GATE_TIER: Record<FeatureGateTier, string> = {
  [FeatureGateTier.COMMUNITY]: "Community",
  [FeatureGateTier.ENTERPRISE]: "Enterprise",
  [FeatureGateTier.TEAM]: "Team",
  [FeatureGateTier.PROFESSIONAL]: "Professional",
};

export const SEAT_PRICE_BY_FEATURE_GATE_TIER: Record<
  FeatureGateTier,
  number | null
> = {
  [FeatureGateTier.COMMUNITY]: 0,
  [FeatureGateTier.ENTERPRISE]: null,
  [FeatureGateTier.TEAM]: 75,
  [FeatureGateTier.PROFESSIONAL]: 36,
};

/**
 * User-level AI credit limits by feature gate tier and org role.
 * UNLIMITED_AI_CREDITS_SENTINEL indicates unlimited credits for that tier/role combination.
 * These are monthly limits that apply per individual user.
 */
export const USER_CREDITS_BY_FEATURE_TIER_AND_ROLE: Record<
  FeatureGateTier,
  Record<OrgRole, Exclude<AiCreditLimit, null>>
> = {
  [FeatureGateTier.COMMUNITY]: {
    [OrgRole.ADMIN]: FREE_TIER_USER_CREDITS,
    [OrgRole.MANAGER]: FREE_TIER_USER_CREDITS,
    [OrgRole.EDITOR]: FREE_TIER_USER_CREDITS,
    [OrgRole.EXPLORER]: 0,
    [OrgRole.MEMBER]: 0,
    [OrgRole.GUEST]: 0,
    [OrgRole.EMBEDDED_USER]: 0,
    [OrgRole.ANONYMOUS]: 0,
  },
  [FeatureGateTier.PROFESSIONAL]: {
    [OrgRole.ADMIN]: 30,
    [OrgRole.MANAGER]: 30,
    [OrgRole.EDITOR]: 30,
    [OrgRole.EXPLORER]: 10,
    [OrgRole.MEMBER]: 0,
    [OrgRole.GUEST]: 0,
    [OrgRole.EMBEDDED_USER]: 0,
    [OrgRole.ANONYMOUS]: 0,
  },
  [FeatureGateTier.TEAM]: {
    [OrgRole.ADMIN]: 40,
    [OrgRole.MANAGER]: 40,
    [OrgRole.EDITOR]: 40,
    [OrgRole.EXPLORER]: 10,
    [OrgRole.MEMBER]: 0,
    [OrgRole.GUEST]: 0,
    [OrgRole.EMBEDDED_USER]: 0,
    [OrgRole.ANONYMOUS]: 0,
  },
  [FeatureGateTier.ENTERPRISE]: {
    [OrgRole.ADMIN]: 60,
    [OrgRole.MANAGER]: 60,
    [OrgRole.EDITOR]: 60,
    [OrgRole.EXPLORER]: 10,
    [OrgRole.MEMBER]: 0,
    [OrgRole.GUEST]: 0,
    [OrgRole.EMBEDDED_USER]: 0,
    [OrgRole.ANONYMOUS]: 0,
  },
};

export const ROLES_WITH_DEFAULT_AI_CREDITS = [
  OrgRole.ADMIN,
  OrgRole.MANAGER,
  OrgRole.EDITOR,
  OrgRole.EXPLORER,
] as const;

export const TIER_ORDERING = [
  FeatureGateTier.COMMUNITY,
  FeatureGateTier.PROFESSIONAL,
  FeatureGateTier.TEAM,
  FeatureGateTier.ENTERPRISE,
];

/**
 * Compares tier in terms of "pricing" order specified in TIER_ORDERING.
 *
 * Returns a postive value if compared tier is an "upgrade".
 *
 * Returns a negative value if compared tier is a "downgrade".
 *
 * Returns 0 if tiers are equal.
 */
export const compareTiers = ({
  comparingTier,
  currentTier,
}: {
  currentTier: FeatureGateTier;
  comparingTier: FeatureGateTier;
}): number => {
  return (
    TIER_ORDERING.indexOf(comparingTier) - TIER_ORDERING.indexOf(currentTier)
  );
};

export const isTierSuperSet = (
  tier: FeatureGateTier,
  targetTier: FeatureGateTier,
): boolean => {
  if (TIER_ORDERING.indexOf(tier) === -1) {
    return false;
  }

  return TIER_ORDERING.indexOf(tier) >= TIER_ORDERING.indexOf(targetTier);
};

export const FeatureGateClickedSourceLiteral = Union(
  Literal("BANNER"),
  Literal("FEATURE_DISABLED"),
);

export const FeatureGateClickedSource = getNormalEnum(
  FeatureGateClickedSourceLiteral,
);
export type FeatureGateClickedSource = Static<
  typeof FeatureGateClickedSourceLiteral
>;

export const FeatureGateCtaLiteral = Union(
  Literal("CONTACT_SALES"),
  Literal("VIEW_PLANS"),
);

export const FeatureGateCta = getNormalEnum(FeatureGateCtaLiteral);
export type FeatureGateCta = Static<typeof FeatureGateCtaLiteral>;

export const FeatureGateClickedEventData = RRecord({
  triggeredFrom: FeatureGateClickedSourceLiteral,
  featureGateType: Array(FeatureGateNameWithAddons),
  orgRole: OrgRoleLiteral.optional(),
  cta: FeatureGateCtaLiteral.optional(),
});

export type FeatureGateClickedEventData = Static<
  typeof FeatureGateClickedEventData
>;
